"use client";

import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ClockFading, ClockIcon, ThumbsUpIcon } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";

export interface SortOption {
	value: string;
	label?: string;
	icon?: JSX.Element;
}

const defaultSortOptions: SortOption[] = [
	{
		label: "Popular",
		value: "popular",
		icon: (
			<ThumbsUpIcon className="size-3 fill-white/30 group-hover:stroke-[3px]" />
		),
	},
	{
		label: "Recent",
		value: "recent",
		icon: (
			<ClockIcon className="size-3 fill-white/30 group-hover:stroke-[3px]" />
		),
	},
	{
		label: "Oldest",
		value: "oldest",
		icon: (
			<ClockFading className="size-3 fill-white/30 group-hover:stroke-[3px]" />
		),
	},
];

interface SortSelectorProps {
	options?: SortOption[];
	className?: string;
	onChange?: (option: SortOption) => void;
}

export function SortSelector({
	options = defaultSortOptions,
	className,
	onChange,
}: SortSelectorProps) {
	const router = useRouter();
	const searchParams = useSearchParams();

	const currentSortParam = searchParams.get("sort") || "";
	const currentSortOption = options.find(
		(option) => option.value === currentSortParam,
	);

	const [selected, setSelected] = useState(currentSortOption || options[0]);

	const handleChange = (option: SortOption | undefined) => {
		setSelected(option || options[0]);
		if (onChange) {
			onChange(option || options[0]);
			return;
		}

		const params = new URLSearchParams(searchParams.toString());
		if (selected) {
			params.set("sort", selected.value);
		} else {
			params.delete("sort");
		}
		// Preserve other search parameters
		const queryString = params.toString();
		const href = queryString ? `/blog?${queryString}` : "/blog";
		router.push(href);
	};

	return (
		<Select
			value={selected.value}
			onValueChange={(value) =>
				handleChange(options.find((option) => option.value === value))
			}
		>
			<SelectTrigger
				className={cn(
					"flex items-center max-w-[100px] z-30 text-xs h-full",
					"px-1.5 gap-1",
					className,
				)}
			>
				<SelectValue placeholder="Sort" />
			</SelectTrigger>
			<SelectContent position="item-aligned">
				<SelectGroup>
					<SelectLabel>Sort by</SelectLabel>
					{options?.map((option) => (
						<SelectItem key={option.value} value={option.value}>
							<div className="flex flex-row items-center gap-1">
								{/* {option.icon} */}
								{option.label || option.value}
							</div>
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
}
